import React, { useEffect, useState } from "react";

import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  Col,
  DatePicker,
  message,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";
import PropTypes from "prop-types";

import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import FiltersWrapper from "../../Components/FiltersWrapper/FiltersWrapper";
import { DEFAULT_DATES_RANGE_OPTIONS } from "../../Utils/constants";
import { isRazorpayXPayoutCurrency } from "../../Utils/utilityFunctions";
import { getWalletWithdrawalRequests } from "../api";
import BulkWithdrawalHandler from "./BulkWithdrawalHandler";
import WalletLedgerTable from "./WalletLedgerTable";
import WithdrawalHandler from "./WithdrawalHandler";

const { Option } = Select;

const razorpayAutomaticPayoutStatuses = [
  "queued",
  "processing",
  "processed",
  "reversed",
  "failed",
];

const currencySymbol = (selectedCurrency) => {
  if (selectedCurrency === "EUR") {
    return <span className="inr">&#x20AC;</span>;
  }
  if (selectedCurrency === "INR") {
    return <span className="inr">&#8377;</span>;
  }
  if (selectedCurrency === "USD") {
    return <span className="inr">&#x24;</span>;
  }
  return <span className="inr">&#8377;</span>;
};

const WalletWithdrawals = ({ creatorCountryType, currency }) => {
  // FIRC foreign-compartment withdrawals are settled in USD (TMG-2249)
  const isFIRCSection = currency === "USD";
  const [withdrawalsData, setWithdrawalsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState("pending");
  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").startOf("day")
  );
  const [endDate, setEndDate] = useState(moment().endOf("day"));
  const [transferMode, setTransferMode] = useState("all");
  const [automaticEligible, setAutomaticEligible] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState("all");
  const [selectedWithdrawals, setSelectedWithdrawals] = useState([]);
  // tracked separately because preserved selections span pages whose rows
  // are no longer in withdrawalsData
  const [selectedNonINRWithdrawals, setSelectedNonINRWithdrawals] = useState(
    []
  );
  const [bulkWithdrawalModalMode, setBulkWithdrawalModalMode] = useState("");
  const [bulkWithdrawalModalVisible, setBulkWithdrawalModalVisible] =
    useState(false);
  const [presentlyViewing, setPresentlyViewing] = useState("");
  const [whitelabelPlanType, setWhitelabelPlanType] = useState(null);
  const [userTier, setUserTier] = useState(null);
  const [resetTableFilter, setResetTableFilter] = useState(false);
  const [tableKey, setTableKey] = useState(Date.now());

  const getWithdrawalRequestsData = async () => {
    setLoading(true);
    try {
      const resp = await getWalletWithdrawalRequests(
        startDate ? startDate.toISOString() : null,
        endDate ? endDate.toISOString() : null,
        status,
        search,
        transferMode === "all" ? undefined : transferMode,
        automaticEligible ? true : undefined,
        payoutStatus === "all" ? undefined : payoutStatus,
        whitelabelPlanType,
        userTier,
        creatorCountryType,
        page,
        limit,
        undefined, // csv
        currency,
        isFIRCSection ? true : undefined // isFIRCWithdrawal: FIRC section lists only FIRC-marked withdrawals (TMG-2249)
      );
      if (resp?.code === 0) {
        setWithdrawalsData(resp.result?.data);
        setTotal(resp.result?.total);
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleWidthdrawalRequestCSVDownload = async () => {
    try {
      const resp = await getWalletWithdrawalRequests(
        startDate.toISOString(),
        endDate.toISOString(),
        status,
        search,
        transferMode === "all" ? undefined : transferMode,
        automaticEligible ? true : undefined,
        payoutStatus === "all" ? undefined : payoutStatus,
        whitelabelPlanType,
        userTier,
        creatorCountryType,
        undefined,
        true,
        true,
        currency,
        isFIRCSection ? true : undefined // isFIRCWithdrawal: FIRC section lists only FIRC-marked withdrawals (TMG-2249)
      );
      fileDownload(
        resp,
        `withdrawal-requestscsv_${moment().format("DD-MM-YYYY")}.csv`
      );
      message.success("CSV download started successfully");
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    }
  };

  const dataTable = () => {
    const columnData = [
      {
        title: "Creator",
        dataIndex: "name",
        key: "name",
        render: (text, record) => (
          <Space size="small" direction="vertical">
            <Space size="small">
              <Avatar src={record?.profilePicUrl} size={32} />
              <Typography
                style={{ fontWeight: 500, fontSize: 14, color: "#1890ff" }}
              >
                {record?.name}
              </Typography>
            </Space>
            <Typography
              style={{
                fontWeight: 300,
                fontSize: 14,
              }}
            >
              {record?.email}
            </Typography>
            <Typography
              style={{
                fontWeight: 300,
                fontSize: 14,
              }}
            >
              {record?.phone}
            </Typography>
            <Typography.Link>{record?.host}</Typography.Link>
            <Typography>
              <strong>
                ({record?.whitelabelPlanType || "N/A"}, {record?.userTier})
              </strong>
            </Typography>
          </Space>
        ),
      },
      {
        title: "Bank Details",
        dataIndex: "accountNumber",
        key: "accountNumber",
        render: (text, record) => {
          const isInternationalCreator =
            record.internationalBankAccount &&
            Object.entries(record.internationalBankAccount).length !== 0;
          return (
            <Space size="small" direction="vertical">
              {!isInternationalCreator ? (
                <>
                  <Typography>
                    <strong>Account Name: </strong>
                    {record?.accountName}
                  </Typography>
                  <Typography>
                    <strong>Account Number: </strong>
                    {record?.accountNumber}
                  </Typography>
                  <Typography>
                    <strong>Bank Name: </strong>
                    {record?.bankName}
                  </Typography>
                  <Typography>
                    <strong>IFSC: </strong>
                    {record?.ifsc}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography>
                    <strong>Account Name: </strong>
                    {record?.internationalBankAccount?.account_holder_name}
                  </Typography>
                  <Typography>
                    <strong>Account Number: </strong>
                    {record?.internationalBankAccount?.account_number}
                  </Typography>
                  <Typography>
                    <strong>IBAN: </strong>
                    {record?.internationalBankAccount?.iban}
                  </Typography>
                  <Typography>
                    <strong>Bank Name: </strong>
                    {record?.internationalBankAccount?.bank_name}
                  </Typography>
                  <Typography>
                    <strong>Bank ID: </strong>
                    {record?.internationalBankAccount?.bank_id}
                  </Typography>
                  <Typography>
                    <strong>Currency: </strong>
                    {record?.internationalBankAccount?.currency}
                  </Typography>
                </>
              )}
            </Space>
          );
        },
      },
      {
        title: "PAN",
        dataIndex: "pan",
        key: "pan",
        render: (text, record) => <Typography>{record?.pan}</Typography>,
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        render: (text, record) => (
          <Typography>
            {currencySymbol(record?.currency)} {text}
          </Typography>
        ),
      },
      // FIRC foreign-compartment withdrawals carry the creator's verified LUT (TMG-2249)
      ...(isFIRCSection
        ? [
            {
              title: "LUT No.",
              dataIndex: "lutNumber",
              key: "lutNumber",
              render: (text) => (
                <Typography.Text copyable={Boolean(text)}>
                  {text || "N/A"}
                </Typography.Text>
              ),
            },
          ]
        : []),
      {
        title: "Approval Status",
        dataIndex: "approvalStatus",
        key: "approvalStatus",
        render: (text, record) => (
          <Space direction="vertical">
            <Typography>{text}</Typography>
            {record?.rejectionReason !== undefined &&
              record?.rejectionReason !== null && (
                <Typography>Reason: {record?.rejectionReason}</Typography>
              )}
            {record?.transferMode === "automatic" && (
              <>
                <Typography>Payout Status: {record?.payoutStatus}</Typography>
                <Typography>
                  Payout Status Description: {record?.payoutStatusDetails}
                </Typography>
              </>
            )}
          </Space>
        ),
      },
      {
        title: "Requested At",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (text, record) => (
          <Space direction="vertical">
            <Typography>
              {moment(record?.createdAt).format("DD/MM/YYYY hh:mm a")}
            </Typography>
            <Typography>({moment(record?.createdAt).fromNow()})</Typography>
            {/* delayed standard withdrawal (TMG-2735): show when the payout
                processor will auto-approve it */}
            {record?.payoutEligibleAt &&
              record?.approvalStatus === "pending" && (
                <Tooltip title="This standard withdrawal is held by the configured delay and will be paid out automatically once eligible">
                  <Typography style={{ fontSize: 12, color: "#fa8c16" }}>
                    Auto-payout eligible{" "}
                    {moment(record?.payoutEligibleAt).fromNow()} (
                    {moment(record?.payoutEligibleAt).format(
                      "DD/MM/YYYY hh:mm a"
                    )}
                    )
                  </Typography>
                </Tooltip>
              )}
          </Space>
        ),
      },
      {
        title: "Actions",
        dataIndex: "actions",
        key: "actions",
        render: (text, record) => (
          <Space direction="vertical">
            <WithdrawalHandler
              withdrawalId={record?._id}
              withdrawalStatus={record?.approvalStatus}
              setResetTableFilter={setResetTableFilter}
              onWithdrawalStatusChange={(newStatus) =>
                setWithdrawalsData(
                  withdrawalsData.reduce(
                    (acc, curr) => [
                      ...acc,
                      curr._id === record?._id
                        ? { ...curr, approvalStatus: newStatus }
                        : curr,
                    ],
                    []
                  )
                )
              }
              withdrawalMode={record?.transferMode}
              refreshTable={getWithdrawalRequestsData}
              bankVerificationStatus={record?.payoutContact?.verificationStatus}
              creatorId={record?.creatorId}
              currency={record?.currency}
            />
          </Space>
        ),
      },
    ];
    return (
      <Table
        key={tableKey}
        dataSource={loading ? [] : withdrawalsData}
        columns={columnData}
        loading={loading}
        pagination={{
          pageSize: limit,
          total,
          onChange: setPage,
          current: page,
          onShowSizeChange: (current, size) => setLimit(size),
        }}
        rowKey={(record) => record?._id}
        expandable={{
          expandedRowRender: (record) => (
            <WalletLedgerTable creatorId={record?.creatorId} />
          ),
          expandedRowKeys: [presentlyViewing],
          onExpand: (expanded, record) => {
            if (expanded) {
              setPresentlyViewing(record?._id);
            } else {
              setPresentlyViewing("");
            }
          },
        }}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedWithdrawals,
          onChange: (selectedRowKeys) => {
            if (selectedRowKeys.length < 18) {
              setSelectedWithdrawals(selectedRowKeys);
              setSelectedNonINRWithdrawals((prev) => {
                const selected = new Set(selectedRowKeys);
                const onThisPage = withdrawalsData
                  .filter(
                    (record) =>
                      selected.has(record?._id) &&
                      !isRazorpayXPayoutCurrency(record?.currency)
                  )
                  .map((record) => record?._id);
                const fromOtherPages = prev.filter((id) => selected.has(id));
                return [...new Set([...fromOtherPages, ...onThisPage])];
              });
            } else {
              message.error(
                "You can only select up to 18 withdrawals, to prevent razorpay rate limits!"
              );
            }
          },
          getCheckboxProps: (record) => {
            if (record?.approvalStatus !== "pending") {
              return { disabled: true };
            }
            if (record?.payoutContact?.verificationStatus !== "completed") {
              return { disabled: true };
            }
          },
          preserveSelectedRowKeys: true,
        }}
        locale={{
          emptyText: loading ? "Loading..." : "No withdrawals found",
        }}
      />
    );
  };

  const resetStates = () => {
    setStartDate(moment().subtract(7, "days").startOf("day"));
    setEndDate(moment().endOf("day"));
    setSearch("");
    setAutomaticEligible(false);
    setTransferMode("all");
    setPayoutStatus("all");
    setWhitelabelPlanType(null);
    setUserTier(null);
    setLimit(10);
    setPage(1);
    setStatus("pending");
    setSelectedWithdrawals([]);
    setSelectedNonINRWithdrawals([]);
    setWithdrawalsData([]);
    setTotal(0);
    setBulkWithdrawalModalMode("");
    setBulkWithdrawalModalVisible(false);
    setPresentlyViewing("");
  };

  useEffect(() => {
    if (status === "pending") {
      setStartDate(moment("2020-12-28 14:30:14.755Z").startOf("day"));
      setPage(1);
    } else {
      setStartDate(moment().subtract(7, "days").startOf("day"));
      setPage(1);
    }
  }, [status]);

  useEffect(() => {
    if (resetTableFilter) return false;
    getWithdrawalRequestsData();
  }, [
    startDate,
    endDate,
    search,
    automaticEligible,
    transferMode,
    payoutStatus,
    whitelabelPlanType,
    userTier,
    creatorCountryType,
    currency,
    page,
    limit,
    resetTableFilter,
  ]);

  useEffect(() => {
    if (resetTableFilter) {
      setLoading(true);
      resetStates();
      setTimeout(() => {
        setResetTableFilter(false);
      }, 1000);
    }
  }, [resetTableFilter]);

  return (
    <>
      {isFIRCSection && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Foreign (USD) FIRC withdrawals"
          description="These are export-earnings withdrawals from FIRC-enabled creators' foreign compartment. They are settled manually via FIRC-compliant banking rails (no RazorpayX auto-payout). A configurable commission (default 2%) is deducted as 'Foreign Currency withdrawal Charges'."
        />
      )}

      <FiltersWrapper>
        <CustomSearchbar
          placeholder="Search by name, email, phone or host"
          searchQuery={search}
          onSearch={(value) => {
            setSearch(value);
            setPage(1);
          }}
          style={{ maxWidth: 500 }}
        />

        <Tooltip title="Refresh">
          <Button
            size="large"
            onClick={getWithdrawalRequestsData}
            style={{ marginLeft: "auto" }}
          >
            <ReloadOutlined />
          </Button>
        </Tooltip>

        <DatePicker.RangePicker
          size="large"
          ranges={DEFAULT_DATES_RANGE_OPTIONS}
          value={startDate && endDate ? [startDate, endDate] : null}
          onChange={(dates) => {
            if (dates && dates.length === 2) {
              const [d1, d2] = dates;
              setStartDate(d1);
              setEndDate(d2);
            } else {
              setStartDate(null);
              setEndDate(null);
            }
          }}
          disabled={status === "pending"}
        />

        <Select
          size="large"
          placeholder="Select approval status"
          onSelect={setStatus}
          value={status}
        >
          <Option value="pending">Pending</Option>
          <Option value="approved">Approved</Option>
          <Option value="rejected">Rejected</Option>
        </Select>

        {status !== "pending" ? (
          <>
            <Select
              size="large"
              placeholder="Select transfer mode"
              value={transferMode}
              onSelect={setTransferMode}
              style={{
                width: 100,
              }}
            >
              <Option value="all">All</Option>
              <Option value="automatic">Automatic</Option>
              <Option value="manual">Manual</Option>
            </Select>
            <Select
              value={payoutStatus}
              onSelect={setPayoutStatus}
              style={{
                width: 100,
              }}
            >
              <Option value="all">All</Option>
              {razorpayAutomaticPayoutStatuses.map((st) => (
                <Option value={st}>{st}</Option>
              ))}
            </Select>
          </>
        ) : (
          <Checkbox
            value={automaticEligible}
            onChange={(e) => setAutomaticEligible(e.target.checked)}
          >
            Automatic Eligible
          </Checkbox>
        )}

        <Button
          className="siteBtn grayBtn"
          onClick={() => handleWidthdrawalRequestCSVDownload()}
        >
          <DownloadOutlined />
          Export CSV
        </Button>
      </FiltersWrapper>

      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col span={24}>
          <Space
            style={{
              width: "100%",
            }}
          >
            <Select
              size="large"
              allowClear
              style={{
                width: "200px",
              }}
              placeholder="Select Plan"
              value={whitelabelPlanType}
              onSelect={(value) => setWhitelabelPlanType(value)}
              onClear={() => setWhitelabelPlanType(null)}
            >
              <Option value="freedom-plan">Freedom plan</Option>
              <Option value="enterprise-plan">Enterprise plan</Option>
            </Select>
            <Select
              size="large"
              allowClear
              style={{
                width: "200px",
              }}
              placeholder="Select Tier"
              value={userTier}
              onSelect={(value) => setUserTier(value)}
              onClear={() => setUserTier(null)}
            >
              <Option value="tier1">Tier 1</Option>
              <Option value="tier2">Tier 2</Option>
              <Option value="tier3">Tier 3</Option>
              <Option value="tier4">Tier 4</Option>
            </Select>
          </Space>
        </Col>
      </Row>

      {selectedWithdrawals?.length > 0 && (
        <Row
          style={{
            marginBottom: "10px",
          }}
        >
          <Space
            style={{
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            <Typography>
              {selectedWithdrawals?.length} Withdrawals selected
            </Typography>
            {/* bulk approval always pays out through RazorpayX, which is INR-only */}
            <Tooltip
              title={
                selectedNonINRWithdrawals.length > 0
                  ? "Bulk approval pays out automatically via RazorpayX (INR only). Deselect non-INR withdrawals and approve them manually."
                  : undefined
              }
            >
              <Button
                type="primary"
                disabled={selectedNonINRWithdrawals.length > 0}
                onClick={() => {
                  setBulkWithdrawalModalMode("approve");
                  setBulkWithdrawalModalVisible(true);
                }}
              >
                Bulk Approve
              </Button>
            </Tooltip>
            <Button
              type="danger"
              onClick={() => {
                setBulkWithdrawalModalMode("reject");
                setBulkWithdrawalModalVisible(true);
              }}
            >
              Bulk Reject
            </Button>
          </Space>
        </Row>
      )}

      {/* <Button onClick={() => setResetTableFilter(true)}>Reset button</Button> */}
      <BulkWithdrawalHandler
        withdrawalIds={selectedWithdrawals}
        modalMode={bulkWithdrawalModalMode}
        setModalMode={setBulkWithdrawalModalMode}
        showModal={bulkWithdrawalModalVisible}
        setShowModal={setBulkWithdrawalModalVisible}
        setResetTableFilter={setResetTableFilter}
        onWithdrawalStatusChange={(withdrawalIds, newStatus) => {
          const withSet = new Set(withdrawalIds);
          setWithdrawalsData(
            withdrawalsData.reduce(
              (acc, curr) => [
                ...acc,
                withSet.has(curr._id)
                  ? { ...curr, approvalStatus: newStatus }
                  : curr,
              ],
              []
            )
          );
        }}
      />
      {dataTable()}
    </>
  );
};

WalletWithdrawals.propTypes = {
  creatorCountryType: PropTypes.string,
  // when 'USD', lists FIRC foreign-compartment withdrawals (TMG-2249)
  currency: PropTypes.string,
};

WalletWithdrawals.defaultProps = {
  creatorCountryType: undefined,
  currency: undefined,
};

export default WalletWithdrawals;
